import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Checkbox,
  HStack,
  VStack,
  Text,
  Box,
  Tooltip,
  useColorModeValue,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useGetVideoQuery, useGetSegmentsQuery } from "services/api";
import ReactPlayer from "react-player";
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useLayoutEffect,
} from "react";
import {
  FaCheck,
  FaPlus,
  FaPenToSquare,
  FaXmark,
  FaMagnifyingGlassPlus,
  FaMagnifyingGlassMinus,
  FaEllipsisVertical,
  FaTrash,
  FaPlay,
} from "react-icons/fa6";
import { API_URL } from "constants";
// Helper function to format time as MM:SS
const formatTimeMMSS = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes
    .toString()
    .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// Helper function to format time as MM:SS.mmm
const formatTimeMMSSmmm = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const milliseconds = Math.floor((seconds % 1) * 1000);
  return `${minutes
    .toString()
    .padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}.${milliseconds.toString().padStart(3, "0")}`;
};

// Helper function to format time as MM:SS.cc
const formatTimeMMSScc = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  const centiseconds = Math.floor((seconds % 1) * 100);
  return `${minutes
    .toString()
    .padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}.${centiseconds.toString().padStart(2, "0")}`;
};

const SegmentTimeline = ({
  segments,
  totalDuration,
  selectedSegments = [],
  onSegmentClick,
  onSegmentUpdate,
  videoPlayerRef,
  onAddSegment,
  onDeleteSegment,
  recommendedSegments = [],
  onPlaySegment,
}) => {
  const [dragState, setDragState] = useState(null);
  const timelineRef = useRef(null);
  const timelineWidthRef = useRef(0);
  const [selectedDraggable, setSelectedDraggable] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Calculate initial visible seconds based on duration and width
  const calculateInitialVisibleSeconds = (duration, width) => {
    // For very short durations, show full duration
    if (duration <= 10) return duration;

    // For short to medium durations, calculate based on available width
    const desiredPixelsPerSecond = 100; // We want at least 100px per second if possible
    if (width && duration * desiredPixelsPerSecond <= width) {
      return duration;
    }

    // Otherwise use our standard scaling
    if (duration <= 30) return 10;
    if (duration <= 60) return 30;
    if (duration <= 300) return 60;
    return 120;
  };

  const [visibleSeconds, setVisibleSeconds] = useState(
    calculateInitialVisibleSeconds(totalDuration, 0)
  );

  // Force recalculation when segments change
  useEffect(() => {
    const timeline = timelineRef.current;
    if (timeline) {
      const width = timeline.clientWidth;
      timelineWidthRef.current = width;
      setVisibleSeconds(calculateInitialVisibleSeconds(totalDuration, width));
      setForceUpdate((prev) => prev + 1);
    }
  }, [segments.length, totalDuration]);

  // Use useLayoutEffect to measure timeline width as soon as possible
  useLayoutEffect(() => {
    const timeline = timelineRef.current;
    if (timeline) {
      const updateTimelineWidth = () => {
        const width = timeline.clientWidth;
        if (width !== timelineWidthRef.current) {
          timelineWidthRef.current = width;
          setVisibleSeconds(
            calculateInitialVisibleSeconds(totalDuration, width)
          );
          setForceUpdate((prev) => prev + 1);
        }
      };

      updateTimelineWidth();

      // Also listen for resize events
      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateTimelineWidth);
      });
      resizeObserver.observe(timeline);

      return () => {
        if (timeline) {
          resizeObserver.unobserve(timeline);
        }
        resizeObserver.disconnect();
      };
    }
  }, [totalDuration]);

  // Calculate pixels per second based on timeline width and visible seconds
  const pixelsPerSecond = useMemo(() => {
    const width = timelineWidthRef.current;
    if (!width) return 100;

    // For short durations, stretch to fill container
    if (totalDuration <= visibleSeconds) {
      return width / totalDuration;
    }

    return width / visibleSeconds;
  }, [totalDuration, visibleSeconds]); // Include forceUpdate to ensure recalculation

  // Adjust visible seconds when timeline width changes
  useEffect(() => {
    const width = timelineWidthRef.current;
    if (width > 0 && totalDuration > 0) {
      const minPixelsPerSecond = 5;
      const maxSecondsAtMinPixels = width / minPixelsPerSecond;

      if (visibleSeconds > maxSecondsAtMinPixels) {
        setVisibleSeconds(Math.floor(maxSecondsAtMinPixels));
      }
    }
  }, [totalDuration, visibleSeconds, forceUpdate]);

  const [segmentInEditMode, setSegmentInEditMode] = useState(null);
  const [temporaryEdits, setTemporaryEdits] = useState({});
  const MIN_VISIBLE_SECONDS = 5;
  const [scrollPosition, setScrollPosition] = useState(0);

  // Calculate maximum visible seconds, rounded up to nearest multiple of 5
  const MAX_VISIBLE_SECONDS = useMemo(() => {
    return Math.ceil(totalDuration / 5) * 5;
  }, [totalDuration]);

  // Track scroll position
  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;

    const handleScroll = () => {
      setScrollPosition(timeline.scrollLeft);
    };

    timeline.addEventListener("scroll", handleScroll);
    return () => timeline.removeEventListener("scroll", handleScroll);
  }, [segments.length]);

  // Use the provided totalDuration instead of calculating from segments
  const maxTime = totalDuration;

  // Generate time markers for the entire timeline
  const timeMarkers = [];
  // Calculate marker interval based on visible seconds
  const markerInterval = useMemo(() => {
    if (visibleSeconds <= 10) return 1; // 1 second intervals when zoomed in
    if (visibleSeconds <= 20) return 2; // 2 second intervals
    if (visibleSeconds <= 30) return 5; // 5 second intervals
    if (visibleSeconds <= 60) return 10; // 10 second intervals
    if (visibleSeconds <= 120) return 15; // 15 second intervals
    if (visibleSeconds <= 300) return 30; // 30 second intervals
    return 60; // 1 minute intervals for very long videos
  }, [visibleSeconds]);

  // Only generate markers up to the actual duration, using the calculated interval
  for (let i = 0; i <= Math.floor(maxTime); i += markerInterval) {
    timeMarkers.push(i);
  }

  // Add the last marker if it's not already included
  if (timeMarkers[timeMarkers.length - 1] !== Math.floor(maxTime)) {
    timeMarkers.push(Math.floor(maxTime));
  }

  const handleMouseDown = (e, segment, type) => {
    if (segmentInEditMode !== segment.uuid) return;
    e.preventDefault();
    e.stopPropagation();

    setSelectedDraggable({ segmentId: segment.uuid, type });
    setDragState({
      segmentId: segment.uuid,
      type,
      startX: e.clientX,
      originalSegment: { ...segment },
    });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragState) return;
      e.preventDefault();

      const timeline = timelineRef.current;
      const rect = timeline.getBoundingClientRect();

      // Calculate time based on mouse position relative to timeline, accounting for scroll
      const mouseX = Math.max(
        0,
        Math.min(
          e.clientX - rect.left + scrollPosition,
          rect.width + scrollPosition
        )
      );
      const time = mouseX / pixelsPerSecond;

      const { segmentId, type } = dragState;
      const currentSegment =
        temporaryEdits[segmentId] || segments.find((s) => s.uuid === segmentId);
      if (!currentSegment) return;

      let updatedSegment;
      if (type === "start") {
        // Ensure start time doesn't exceed end time
        const newStart = Math.min(Math.max(0, time), currentSegment.end - 0.1);
        updatedSegment = { ...currentSegment, start: newStart };
      } else {
        // Ensure end time doesn't precede start time
        const newEnd = Math.max(
          Math.min(time, totalDuration),
          currentSegment.start + 0.1
        );
        updatedSegment = { ...currentSegment, end: newEnd };
      }

      // Store the temporary edit instead of committing it
      setTemporaryEdits((prev) => ({
        ...prev,
        [segmentId]: updatedSegment,
      }));

      if (videoPlayerRef.current) {
        videoPlayerRef.current.seekTo(
          type === "start" ? updatedSegment.start : updatedSegment.end,
          "seconds"
        );
      }
    },
    [
      dragState,
      scrollPosition,
      pixelsPerSecond,
      temporaryEdits,
      segments,
      videoPlayerRef,
      totalDuration,
    ]
  );

  const handleAcceptEdit = (segmentId) => {
    const editedSegment = temporaryEdits[segmentId];
    if (editedSegment) {
      onSegmentUpdate(segmentId, editedSegment);
    }
    setSegmentInEditMode(null);
    setTemporaryEdits((prev) => {
      const newEdits = { ...prev };
      delete newEdits[segmentId];
      return newEdits;
    });
  };

  const handleRejectEdit = (segmentId) => {
    setSegmentInEditMode(null);
    setTemporaryEdits((prev) => {
      const newEdits = { ...prev };
      delete newEdits[segmentId];
      return newEdits;
    });
  };

  const handleMouseUp = () => {
    setDragState(null);
  };

  useEffect(() => {
    if (dragState) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove]);

  const handlePlaySegment = (segment) => {
    if (onPlaySegment) {
      onPlaySegment(segment);
    }
  };

  const handleSegmentClick = (segment) => {
    if (segmentInEditMode !== segment.uuid) {
      onPlaySegment(segment);
    }
  };

  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const emptyTextColor = useColorModeValue("gray.500", "gray.400");
  const menuBg = useColorModeValue("white", "gray.800");
  const menuBorderColor = useColorModeValue("gray.200", "gray.700");
  const editIconColor = useColorModeValue("gray.500", "gray.400");
  useColorModeValue("gray.500", "gray.400");

  if (!segments.length) {
    return (
      <VStack align="center" justify="center" py={8} spacing={4}>
        <Text color={emptyTextColor} fontSize="md" textAlign="center">
          {recommendedSegments.length > 0
            ? "Choose a recommended segment or add a new segment to get started"
            : "Add a new segment to get started"}
        </Text>
        <Button
          leftIcon={<FaPlus />}
          onClick={onAddSegment}
          size="sm"
          variant="outline"
        >
          Add Segment
        </Button>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={4} width="100%">
      <Box
        bg="white"
        borderColor="gray.200"
        borderRadius="lg"
        borderWidth="1px"
        boxShadow="sm"
        maxWidth="100%"
        overflow="visible"
        p={4}
        position="relative"
        width="100%"
      >
        <HStack justify="end" mb={2}>
          <HStack spacing={2}>
            <Tooltip label="Zoom Out" placement="top">
              <IconButton
                aria-label="Zoom out"
                icon={<FaMagnifyingGlassMinus />}
                isDisabled={visibleSeconds >= MAX_VISIBLE_SECONDS}
                onClick={() =>
                  setVisibleSeconds((prev) =>
                    Math.min(MAX_VISIBLE_SECONDS, prev + 5)
                  )
                }
                size="sm"
                variant="ghost"
              />
            </Tooltip>
            <Tooltip label="Zoom In" placement="top">
              <IconButton
                aria-label="Zoom in"
                icon={<FaMagnifyingGlassPlus />}
                isDisabled={visibleSeconds <= MIN_VISIBLE_SECONDS}
                onClick={() =>
                  setVisibleSeconds((prev) =>
                    Math.max(MIN_VISIBLE_SECONDS, prev - 5)
                  )
                }
                size="sm"
                variant="ghost"
              />
            </Tooltip>
          </HStack>
        </HStack>
        <HStack align="start" spacing={0}>
          {/* Checkbox column */}
          <Box
            bg="white"
            left={0}
            pb={4}
            position="sticky"
            pt="40px"
            width="100px"
            zIndex={2}
          >
            <VStack spacing={2} width="100%">
              {segments.map((segment) => {
                const isSelected = selectedSegments.some(
                  (s) => s.uuid === segment.uuid
                );
                const isThisSegmentInEditMode =
                  segmentInEditMode === segment.uuid;
                const editedSegment = temporaryEdits[segment.uuid] || segment;
                return (
                  <HStack height="36px" key={segment.uuid} spacing={2}>
                    {isThisSegmentInEditMode ? (
                      <>
                        <IconButton
                          aria-label="Accept edit"
                          colorScheme="green"
                          icon={<Icon as={FaCheck} />}
                          onClick={() => handleAcceptEdit(segment.uuid)}
                          size="xs"
                        />
                        <IconButton
                          aria-label="Reject edit"
                          colorScheme="red"
                          icon={<Icon as={FaXmark} />}
                          onClick={() => handleRejectEdit(segment.uuid)}
                          size="xs"
                        />
                      </>
                    ) : (
                      <>
                        <Checkbox
                          isChecked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSegmentClick(segment);
                          }}
                          size="lg"
                        />
                        <Tooltip label="Play segment" placement="top">
                          <IconButton
                            _hover={{
                              bg: hoverBg,
                            }}
                            aria-label="Play segment"
                            color="gray.600"
                            icon={<Icon as={FaPlay} />}
                            onClick={() => handlePlaySegment(editedSegment)}
                            size="xs"
                            variant="ghost"
                          />
                        </Tooltip>
                        <Menu isLazy>
                          <MenuButton
                            _hover={{
                              bg: hoverBg,
                            }}
                            aria-label="Options"
                            as={IconButton}
                            color="gray.600"
                            icon={<Icon as={FaEllipsisVertical} />}
                            size="xs"
                            variant="ghost"
                          />
                          <MenuList
                            bg={menuBg}
                            borderColor={menuBorderColor}
                            fontSize="sm"
                            minWidth="100px"
                            py={0}
                            width="100px"
                          >
                            <MenuItem
                              _hover={{
                                bg: hoverBg,
                              }}
                              fontSize="sm"
                              icon={
                                <Icon
                                  as={FaPenToSquare}
                                  color={editIconColor}
                                />
                              }
                              onClick={() => {
                                if (isThisSegmentInEditMode) {
                                  setSegmentInEditMode(null);
                                } else {
                                  setSegmentInEditMode(segment.uuid);
                                  setTemporaryEdits((prev) => ({
                                    ...prev,
                                    [segment.uuid]: { ...segment },
                                  }));
                                }
                              }}
                              py={1}
                            >
                              {isThisSegmentInEditMode
                                ? "Exit Edit Mode"
                                : "Edit"}
                            </MenuItem>
                            <MenuItem
                              _hover={{
                                bg: hoverBg,
                              }}
                              fontSize="sm"
                              icon={<Icon as={FaTrash} color="red.500" />}
                              onClick={() => onDeleteSegment(segment.uuid)}
                              py={1}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </>
                    )}
                  </HStack>
                );
              })}
            </VStack>
          </Box>
          {/* Scrollable timeline area */}
          <Box
            css={{
              "&::-webkit-scrollbar": {
                height: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
            flex={1}
            overflowX="auto"
            ref={timelineRef}
          >
            {/* Time markers */}
            <Box
              height="24px"
              mb={4}
              position="relative"
              width={`${maxTime * pixelsPerSecond}px`}
            >
              {timeMarkers.map((time) => (
                <Box
                  color="gray.600"
                  fontSize="xs"
                  key={time}
                  left={`${time * pixelsPerSecond}px`}
                  position="absolute"
                  textAlign="center"
                  transform={time === 0 ? "translateX(0)" : "translateX(-50%)"}
                >
                  {time === 0 ? "0" : formatTimeMMSS(time)}
                  <Box
                    bg="gray.300"
                    height="8px"
                    left={time === 0 ? "0" : "50%"}
                    position="absolute"
                    top="100%"
                    transform={time === 0 ? "none" : "translateX(-50%)"}
                    width="1px"
                  />
                </Box>
              ))}
              {/* Time axis line */}
              <Box
                bg="gray.300"
                bottom="0"
                height="1px"
                left="0"
                position="absolute"
                right="0"
              />
            </Box>

            {/* Segments - one per row */}
            <VStack
              align="stretch"
              spacing={2}
              width={`${maxTime * pixelsPerSecond}px`}
            >
              {segments.map((segment) => {
                const editedSegment = temporaryEdits[segment.uuid] || segment;
                const startPixels = editedSegment.start * pixelsPerSecond;
                const widthPixels =
                  (editedSegment.end - editedSegment.start) * pixelsPerSecond;
                const isThisSegmentInEditMode =
                  segmentInEditMode === editedSegment.uuid;

                return (
                  <>
                    <Box
                      bg="gray.50"
                      borderRadius="md"
                      height="36px"
                      key={editedSegment.uuid}
                      position="relative"
                    >
                      <Box
                        _hover={{
                          boxShadow: "md",
                          "& .play-icon": {
                            opacity: 1,
                          },
                        }}
                        bgColor="blue.400"
                        borderRadius="md"
                        borderStyle="solid"
                        boxShadow="sm"
                        cursor={isThisSegmentInEditMode ? "default" : "pointer"}
                        height="28px"
                        left={`${startPixels}px`}
                        onClick={() =>
                          !isThisSegmentInEditMode &&
                          handleSegmentClick(editedSegment)
                        }
                        position="absolute"
                        sx={{
                          "@keyframes pulse": {
                            "0%": {
                              boxShadow: "0 0 0 0 rgba(66, 153, 225, 0.4)",
                            },
                            "70%": {
                              boxShadow: "0 0 0 10px rgba(66, 153, 225, 0)",
                            },
                            "100%": {
                              boxShadow: "0 0 0 0 rgba(66, 153, 225, 0)",
                            },
                          },
                        }}
                        top="4px"
                        transition="all 0.2s"
                        width={`${widthPixels}px`}
                      >
                        {!isThisSegmentInEditMode && (
                          <Box
                            className="play-icon"
                            color="white"
                            fontSize="lg"
                            left="50%"
                            opacity={0}
                            position="absolute"
                            top="50%"
                            transform="translate(-50%, -50%)"
                            transition="opacity 0.2s"
                          >
                            <FaPlay />
                          </Box>
                        )}
                        {!!isThisSegmentInEditMode && (
                          <>
                            <Box
                              _before={{
                                content: '""',
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: "4px",
                                height: "16px",
                                background:
                                  "linear-gradient(to right, #718096 1px, transparent 1px)",
                                backgroundSize: "2px 2px",
                                opacity: 0.5,
                                transition: "opacity 0.2s",
                              }}
                              _hover={{
                                bg: "blue.50",
                                "&::before": {
                                  opacity: 1,
                                },
                              }}
                              bg="white"
                              border="1px solid"
                              borderRadius="sm"
                              bottom="0"
                              cursor="ew-resize"
                              left="0"
                              onMouseDown={(e) =>
                                handleMouseDown(e, editedSegment, "start")
                              }
                              position="absolute"
                              top="0"
                              width="12px"
                              zIndex={2}
                            />
                            <Box
                              _before={{
                                content: '""',
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: "4px",
                                height: "16px",
                                background:
                                  "linear-gradient(to right, #718096 1px, transparent 1px)",
                                backgroundSize: "2px 2px",
                                opacity: 0.5,
                                transition: "opacity 0.2s",
                              }}
                              _hover={{
                                bg: "blue.50",
                                "&::before": {
                                  opacity: 1,
                                },
                              }}
                              bg="white"
                              border="1px solid"
                              borderColor={
                                selectedDraggable?.segmentId ===
                                  editedSegment.uuid &&
                                selectedDraggable?.type === "end"
                                  ? "blue.500"
                                  : "transparent"
                              }
                              borderRadius="sm"
                              bottom="0"
                              cursor="ew-resize"
                              onMouseDown={(e) =>
                                handleMouseDown(e, editedSegment, "end")
                              }
                              position="absolute"
                              right="0"
                              top="0"
                              width="12px"
                              zIndex={2}
                            />
                            <Box
                              color="gray.600"
                              fontSize="10px"
                              left="0"
                              position="absolute"
                              top="-15px"
                              transform="translateX(-50%)"
                              zIndex={3}
                            >
                              {formatTimeMMSScc(editedSegment.start)}
                            </Box>
                            <Box
                              color="gray.600"
                              fontSize="10px"
                              position="absolute"
                              right="0"
                              top="-15px"
                              transform="translateX(50%)"
                              zIndex={3}
                            >
                              {formatTimeMMSScc(editedSegment.end)}
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  </>
                );
              })}
            </VStack>
          </Box>
        </HStack>
        <Button
          leftIcon={<FaPlus />}
          mt={4}
          onClick={onAddSegment}
          size="sm"
          variant="outline"
          width="100%"
        >
          Add Segment
        </Button>
      </Box>
    </VStack>
  );
};

export const VideoModal = ({
  onClose,
  videoId,
  onSegmentUpdate,
  selectedSegments,
  setSelectedSegments,
  recommendedSegments = [],
  onAddSegment,
  onDeleteSegment,
}) => {
  const { projectId } = useParams();
  const { data: video } = useGetVideoQuery({
    projectId,
    videoId,
  });
  const firstThumbnailUrl = video?.hls?.thumbnail_urls?.[0];
  const filename = video?.system_metadata?.filename;
  const videoPlayerRef = useRef(null);
  const [currentPlayingSegment, setCurrentPlayingSegment] = useState(null);

  const { data: segmentData } = useGetSegmentsQuery({
    projectId,
    filename,
  });
  const [isEditing, setIsEditing] = useState(false);

  const totalDuration = video?.system_metadata?.duration ?? 0;

  const handlePlaySegment = (segment) => {
    if (videoPlayerRef.current) {
      setCurrentPlayingSegment(segment);
      videoPlayerRef.current.seekTo(segment.start, "seconds");
      videoPlayerRef.current.getInternalPlayer().play();
    }
  };

  const handleProgress = (state) => {
    if (
      currentPlayingSegment &&
      state.playedSeconds >= currentPlayingSegment.end
    ) {
      videoPlayerRef.current.getInternalPlayer().pause();
      setCurrentPlayingSegment(null);
    }
  };

  const handleSegmentSelection = (segment) => {
    setSelectedSegments((prev) => {
      const isSelected = prev.some((s) => s.uuid === segment.uuid);
      if (isSelected) {
        return prev.filter((s) => s.uuid !== segment.uuid);
      } else {
        return [...prev, segment];
      }
    });
  };

  const handleAddSegment = () => {
    // Calculate middle of the timeline
    const middle = totalDuration / 2;
    const segmentDuration = 5; // 5 second segment
    const start = Math.max(0, middle - segmentDuration / 2);
    const end = Math.min(totalDuration, middle + segmentDuration / 2);

    const newSegment = {
      start,
      end,
      confidence: null,
      score: null,
      thumbnailUrl: firstThumbnailUrl,
      filename,
      parentId: videoId,
      uuid: self.crypto.randomUUID(),
    };

    onAddSegment(videoId, newSegment);
  };

  const handleSegmentUpdate = (segmentId, updatedSegment) => {
    onSegmentUpdate(videoId, segmentId, updatedSegment);

    // Update selectedSegments if the modified segment is selected
    setSelectedSegments((prev) =>
      prev.map((segment) =>
        segment.uuid === segmentId
          ? { ...segment, start: updatedSegment.start, end: updatedSegment.end }
          : segment
      )
    );
  };

  const handleDeleteSegment = (segmentId) => {
    // Remove from selectedSegments if it's selected
    const segmentToDelete = segmentData?.segments?.find(
      (s) => s.uuid === segmentId
    );
    if (segmentToDelete && selectedSegments.some((s) => s.uuid === segmentId)) {
      handleSegmentSelection(segmentToDelete);
    }

    onDeleteSegment(videoId, segmentId);
  };

  const handleConvertToCustomSegment = (recommendedSegment) => {
    const newSegment = {
      start: recommendedSegment.start,
      end: recommendedSegment.end,
      confidence: recommendedSegment.confidence,
      score: recommendedSegment.score,
      thumbnailUrl: firstThumbnailUrl,
      filename,
      parentId: videoId,
      uuid: self.crypto.randomUUID(),
    };
    onAddSegment(videoId, newSegment);
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Modal
      isCentered
      isOpen
      maxH="90vh"
      onClose={onClose}
      scrollBehavior="inside"
      size="6xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={4}>
            <span>{filename}</span>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <HStack align="start" spacing={8}>
            <Box flex="0 0 auto" width="40%">
              <VStack align="stretch" spacing={4}>
                <ReactPlayer
                  controls
                  height="auto"
                  onProgress={handleProgress}
                  ref={videoPlayerRef}
                  url={`${API_URL}/video/${projectId}/${filename}`}
                  width="100%"
                />
                {recommendedSegments.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      Recommended Segments
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {recommendedSegments.map((segment) => (
                        <Box
                          bg={cardBg}
                          border="1px"
                          borderColor={borderColor}
                          borderRadius="md"
                          key={segment.uuid}
                          p={2}
                        >
                          <HStack spacing={4}>
                            <VStack align="start" flex={1} spacing={1}>
                              <HStack>
                                <Text fontWeight="bold">
                                  {segment.confidence === "high" && (
                                    <Text as="span" color="green.500">
                                      High Confidence
                                    </Text>
                                  )}
                                  {segment.confidence === "medium" && (
                                    <Text as="span" color="orange.500">
                                      Medium Confidence
                                    </Text>
                                  )}
                                  {segment.confidence === "low" && (
                                    <Text as="span" color="red.500">
                                      Low Confidence
                                    </Text>
                                  )}
                                </Text>
                              </HStack>
                              <HStack spacing={4}>
                                <Text fontSize="sm">
                                  Start: {formatTimeMMSSmmm(segment.start)}
                                </Text>
                                <Text fontSize="sm">
                                  End: {formatTimeMMSSmmm(segment.end)}
                                </Text>
                                <Text fontSize="sm">
                                  Duration:{" "}
                                  {formatTimeMMSSmmm(
                                    segment.end - segment.start
                                  )}
                                </Text>
                              </HStack>
                            </VStack>
                            <VStack spacing={2}>
                              <Button
                                colorScheme="blue"
                                leftIcon={<FaPlay />}
                                onClick={() => handlePlaySegment(segment)}
                                size="sm"
                                variant="outline"
                              >
                                Play Segment
                              </Button>
                              <Button
                                leftIcon={<FaPlus />}
                                onClick={() =>
                                  handleConvertToCustomSegment(segment)
                                }
                                size="sm"
                                variant="outline"
                              >
                                Add Segment
                              </Button>
                            </VStack>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>
            <Box flex="1" maxWidth="60%" overflow="hidden">
              <VStack align="stretch" spacing={4}>
                <SegmentTimeline
                  isEditing={isEditing}
                  onAddSegment={handleAddSegment}
                  onDeleteSegment={handleDeleteSegment}
                  onPlaySegment={handlePlaySegment}
                  onSegmentClick={handleSegmentSelection}
                  onSegmentUpdate={handleSegmentUpdate}
                  onToggleEdit={() => setIsEditing(!isEditing)}
                  recommendedSegments={recommendedSegments}
                  segments={segmentData?.segments || []}
                  selectedSegments={selectedSegments}
                  totalDuration={totalDuration}
                  videoPlayerRef={videoPlayerRef}
                />
              </VStack>
            </Box>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
